import { BaseApi } from "@/api/base";
import type { Response } from "@/api/client";
import type { ChangeTruckStatusData, TruckDto, TruckRoutesResponse } from "@/api/trucks/dto/truck.dto";
import type { ListResponseDto } from "@/api/dto/list-response.dto";
import type { Locations } from "@/api/trucks/dto/response/locations";
import type { RouteStatus } from "@/constants/trip";

export class TrucksApi extends BaseApi {
  private trucksListController: AbortController | undefined;

  async findTrucks(options = {}, abortable = true): Promise<Response<ListResponseDto<TruckDto>>> {
    if (abortable && this.trucksListController) {
      this.trucksListController.abort();
    }
    this.trucksListController = new AbortController();

    const response = await this.client.get<ListResponseDto<TruckDto>>("trucks", options, {
      signal: abortable ? this.trucksListController.signal : undefined,
    });
    this.trucksListController = undefined;
    return response;
  }

  findTruckById(truckId: number | string): Promise<Response<TruckDto>> {
    return this.client.get<TruckDto>("trucks/" + truckId);
  }

  reserveTruck(truckId: number, dispatcherId: number, minutes: number): Promise<Response<TruckDto>> {
    return this.client.post<TruckDto>("trucks/" + truckId + "/reserve", {
      dispatcherId: dispatcherId,
      minutes,
    });
  }

  async upsertTruck(truck: any): Promise<Response<TruckDto>> {
    return this.client.save<TruckDto>("/trucks", truck);
  }

  updateTruck(truckId: number, data: any): Promise<Response<TruckDto>> {
    return this.client.patch<TruckDto>("trucks/" + truckId, data);
  }

  updateTruckStatus(truckId: number, data: ChangeTruckStatusData): Promise<Response<TruckDto>> {
    return this.client.patch<TruckDto>("trucks/" + truckId + "/status", data);
  }

  getTruckLocations(
    truckId: number,
    dateFrom: string | null = null,
    dateTo: string | null = null,
  ): Promise<Response<Locations>> {
    return this.client.get<Locations>("trucks/" + truckId + "/locations", {
      timeFrom: dateFrom,
      timeTo: dateTo,
    });
  }

  archiveTruck(truckId: number): Promise<Response<void>> {
    return this.client.post("/trucks/" + truckId + "/archive", {});
  }

  unarchiveTruck(truckId: number): Promise<Response<void>> {
    return this.client.post("/trucks/" + truckId + "/unarchive", {});
  }

  getTrackLink(truckId: number, date: string): Promise<Response<{ link: string }>> {
    return this.client.post("/trucks/" + truckId + "/tracking-link", {
      validUntil: date,
    });
  }

  handleEnableTracking(truckId: number, value: boolean): Promise<Response<string>> {
    return this.client.post("/trucks/" + truckId + "/tracking", {
      enableTracking: value,
    });
  }

  getTruckRoutes(truckId: number | string, status: RouteStatus[]): Promise<Response<TruckRoutesResponse>> {
    return this.client.get("/trucks/" + truckId + "/routes", {
      status,
    });
  }
}
